import React, { useEffect, useRef, useState } from 'react';
import { withAlert } from 'react-alert';
import { compose } from 'redux';
import { Button, Card, Divider, Form, Input, Rate, Row, Col, Select, Typography } from 'antd';
import { StarFilled } from '@ant-design/icons';
import Loading from '../../components/loading';
import './RatingPage.css';

const { TextArea } = Input;
const { Title, Text } = Typography;

const RATING_LABELS = { 1: 'Poor', 2: 'Fair', 3: 'Good', 4: 'Very Good', 5: 'Excellent' };

const defaultState = {
	caterer_id: null,
	caterer_name: null,
	rating_1: 0,
	rating_2: 0,
	rating_3: 0,
	other_comments: null,
	disableButton: true,
	loadingSubmit: false
};

function RatingPage(props) {
	const { pageProps, getCaterers } = props;
	const { caterers, loading } = pageProps;

	const [form] = Form.useForm();
	const prevPropsRef = useRef();
	const [formState, setFormState] = useState(defaultState);

	const { caterer_id, rating_1, rating_2, rating_3 } = formState;

	useEffect(() => {
		getCaterers();
		return () => props.resetPage();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (pageProps && pageProps.meta) {
			const { meta } = pageProps;
			if (!prevPropsRef.current) {
				const alertOptions = { type: meta.code === 200 ? 'success' : 'error' };
				props.alert.show(meta.message, alertOptions);
				props.resetMeta();
			}
			if (prevPropsRef.current && prevPropsRef.current.meta !== meta) {
				const alertOptions = { type: meta.code === 200 ? 'success' : 'error' };
				props.alert.show(meta.message, alertOptions);
				props.resetMeta();
			}
			prevPropsRef.current = pageProps;
		}
	}, [pageProps, pageProps.meta, props]);

	useEffect(() => {
		caterer_id && rating_1 > 0 && rating_2 > 0 && rating_3 > 0
			? setFormState(prev => ({ ...prev, disableButton: false }))
			: setFormState(prev => ({ ...prev, disableButton: true }));
	}, [caterer_id, rating_1, rating_2, rating_3]);

	const catererOptions = (caterers || []).map(c => ({ value: c._id, label: c.name }));

	const onSubmit = () => {
		if (formState.disableButton) return;
		setFormState(prev => ({ ...prev, loadingSubmit: true }));
		props.onSubmit(formState);
	};

	const renderStarField = (label, fieldName, value) => (
		<Form.Item
			label={
				<Text strong style={{ fontSize: 15 }}>
					{label} <span style={{ color: '#ff4d4f' }}>*</span>
				</Text>
			}
			style={{ marginBottom: 20 }}
		>
			<div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
				<Rate
					value={value}
					onChange={val => setFormState(prev => ({ ...prev, [fieldName]: val }))}
					style={{ fontSize: 34, color: '#faad14' }}
					character={<StarFilled />}
				/>
				{value > 0 && (
					<Text
						style={{
							color: '#595959',
							fontSize: 14,
							fontWeight: 500,
							background: '#fffbe6',
							border: '1px solid #ffe58f',
							borderRadius: 4,
							padding: '2px 10px'
						}}
					>
						{RATING_LABELS[value]}
					</Text>
				)}
			</div>
		</Form.Item>
	);

	if (loading) {
		return (
			<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
				<Loading />
			</div>
		);
	}

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				padding: '32px 16px 80px',
				minHeight: '100vh',
				backgroundColor: '#f0f2f5'
			}}
		>
			<div style={{ width: '100%', maxWidth: 700 }}>
				<div
					style={{
						backgroundColor: '#003366',
						borderRadius: '8px 8px 0 0',
						padding: '28px 40px',
						textAlign: 'center'
					}}
				>
					<Title level={2} style={{ color: '#ffffff', margin: 0, fontFamily: 'Arial' }}>
						Catering Outlet Rating
					</Title>
					<Title level={2} style={{ color: '#ffffff', margin: 0, fontFamily: 'Arial' }}>
						餐飲店評分
					</Title>
					<Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: 14, marginTop: 6, display: 'block' }}>
						Help us improve your dining experience by sharing your feedback.
					</Text>
					<Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: 14, marginTop: 6, display: 'block' }}>
						請分享您的寶貴意見，幫助我們改善您的用餐體驗。
					</Text>
				</div>

				<Card
					bordered={false}
					style={{ borderRadius: '0 0 8px 8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
					bodyStyle={{ padding: '36px 40px' }}
				>
					<Form form={form} layout="vertical">
						<Form.Item
							name="caterer_id"
							label={
								<Text strong style={{ fontSize: 15 }}>
									Catering Outlet 餐飲店<span style={{ color: '#ff4d4f' }}>*</span>
								</Text>
							}
							style={{ marginBottom: 24 }}
						>
							<Select
								placeholder="Please select a catering outlet 請選擇餐飲店"
								options={catererOptions}
								size="large"
								style={{ width: '100%' }}
								className="multiline-select"
								showSearch
								optionFilterProp="label"
								onChange={(val, option) => {
									setFormState(prev => ({
										...prev,
										caterer_id: val,
										caterer_name: option ? option.label : null
									}));
								}}
							/>
						</Form.Item>

						<Divider style={{ margin: '4px 0 20px' }} />

						{renderStarField('Food Quality 食物質素', 'rating_1', rating_1)}
						{renderStarField('Hygiene 衛生', 'rating_2', rating_2)}
						{renderStarField('Service 服務', 'rating_3', rating_3)}

						<Divider style={{ margin: '4px 0 20px' }} />

						<Form.Item
							name="other_comments"
							label={<Text style={{ fontSize: 15 }}>Other Comments 其他意見 <Text type="secondary">(optional 可選擇填寫與否)</Text></Text>}
						>
							<TextArea
								rows={4}
								placeholder="Share any additional feedback 分享其他意見..."
								onChange={e => setFormState(prev => ({ ...prev, other_comments: e.target.value }))}
								style={{ resize: 'none' }}
							/>
						</Form.Item>

						<Button
							type="primary"
							size="large"
							loading={formState.loadingSubmit}
							disabled={formState.disableButton || formState.loadingSubmit}
							onClick={onSubmit}
							block
							style={{
								backgroundColor: '#003366',
								borderColor: '#003366',
								color: '#ffffff',
								fontWeight: 600,
								marginTop: 8
							}}
						>
							Submit 提交
						</Button>
						<div style={{ textAlign: 'center', marginTop: 12 }}>
							<Text
								onClick={() => props.history.push('/web-form')}
								style={{ fontSize: 13, color: '#1890ff', cursor: 'pointer', textDecoration: 'underline' }}
							>
								Submit detailed comments or feedback 提交詳細評語或意見
							</Text>
						</div>
					</Form>
				</Card>
			</div>
		</div>
	);
}

export default compose(withAlert())(RatingPage);
